import { Children, cloneElement, isValidElement } from 'react';
import styled from '@emotion/styled';
import { useWindowSize, isMobile } from 'app/spacing';
import BaseModal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'components/Modals';
import { StyledWizardBody } from './styles';
import Wizards from './wizards';
import Section from './section';
import css from '@emotion/css';

const GenericWizard = (props) => {
  const { wizard } = props;
  const Wizard = Wizards[wizard];

  return (
    <StyledWizardBody className="GenericWizardBody">
      <Wizard isActive {...props} />
    </StyledWizardBody>
  );
};

export default GenericWizard;

// Example wizard toolkit structure/usage:
// Pass _unwrapChildren to a wrapper component to have its children be assigned section indices,
// but not the wrapper itself.
//
//    <GenericWizardBaseModal>
//      <GenericWizardModalHeader>Heading</GenericWizardModalHeader>
//      <GenericWizardModalBody>
//        <GenericWizardSection />
//        <Wrapper _unwrapChildren>
//          <GenericWizardSection />
//          <GenericWizardSection />
//        </Wrapper>
//        <GenericWizardSection />
//      </GenericWizardModalBody>
//      <GenericWizardModalFooter>
//        <Button>Save</Button>
//      </GenericWizardModalFooter>
//    </GenericWizardBaseModal>;
//

const getSections = (children, index = 0, length = 0) => {
  const sections = [];
  const list = Children.toArray(children).filter(isValidElement);

  for (const child of list) {
    if (child.props._unwrapChildren) {
      const [innerSections, innerIndex] = getSections(
        child.props.children,
        index,
        length
      );
      index = innerIndex;
      sections.push(cloneElement(child, { children: innerSections }));
    } else {
      sections.push(
        cloneElement(child, { index, isLastIndex: index === length - 1 })
      );
      index++;
    }
  }

  return [sections, index];
};

const countSections = (children) => {
  let count = 0;
  const list = Children.toArray(children).filter(isValidElement);

  for (const child of list) {
    if (child.props._unwrapChildren) {
      count += countSections(child.props.children);
    } else {
      count++;
    }
  }

  return count;
};

const produceSections = (children) => {
  const [sections] = getSections(children, 0, countSections(children));
  return sections;
};

export function GenericWizardBody({
  className,
  children,
  containerRef,
  ...props
}) {
  return (
    <StyledWizardBody
      className={`GenericWizardBody ${className || ''}`}
      {...props}
      ref={containerRef}
    >
      {produceSections(children)}
    </StyledWizardBody>
  );
}

export const GenericWizardSection = styled(Section)`
  margin-top: 17px;
  margin-bottom: 37px;
  min-width: 0; // Helps ensures inline contents don't get go wider than 100%, e.g. inline select menu options
  ${({ leavePAlone }) =>
    leavePAlone
      ? null
      : css`
          p {
            margin-bottom: unset; // Undo an overreaching style from Section
          }
        `}

  &:last-child {
    margin-bottom: 0; // No extra padding between bottom of section and footer
  }
`;

// Note that this doesn't use the BasicBaseModal, a bit of an outlier currently.
// Also not that the dashlet wizard and field builder modal don't use these components at the time of writing,
// in case you are in charge of changing all "standard wizard" styles.

export function GenericWizardBaseModal(props) {
  return <BaseModal size="medium" fitContent={false} {...props} />;
}

export const GenericWizardModalHeader = ModalHeader;

export function GenericWizardModalBody({ children, containerRef, ...props }) {
  const { width } = useWindowSize();

  return (
    <ModalBody
      typeOfContent={isMobile(width) ? 'wizard-mobile' : 'wizard'}
      {...props}
    >
      <GenericWizardBody containerRef={containerRef}>
        {children}
      </GenericWizardBody>
    </ModalBody>
  );
}

// In the future this may have showBorders={false}, factored out here for control in the future.

export const GenericWizardModalFooter = ModalFooter;
